import { AddIndexResReadAdminPointBetSwapDto, ResReadAdminPointBetSwapDto } from '@/types';
import { Box, Button, Card, CardContent, Divider, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import Header from '@/component/Header';
import { BetSwapListColumns } from '@/component/point/table/ColumnData';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridSelectionModel, GridToolbar, GridToolbarExport } from '@mui/x-data-grid';
import { useTheme } from '@mui/material';

import { packBigNumbers } from '@/lib/BigNumbers';
import { betSwapStatusFilter, betSwapTypeFilter } from '@/lib/filter/bet';
import { POINT_BET_SWAP_STATUS, POINT_BET_SWAP_TYPE } from '@/lib/common/name-space';
import Loading from '@/component/mui/Backdrop';
import { toast } from 'react-toastify';
import { updateSwap } from '@/api/bet/update';
import { readSwapList } from '@/api/bet/read';

export default function SwapList() {
  const [list, setList] = useState<AddIndexResReadAdminPointBetSwapDto[]>([]);
  const [selectedRows, setSelectedRows] = useState<GridSelectionModel>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hashInputs, setHashInputs] = useState<Record<string, string>>({});
  const theme = useTheme();

  const handleHashChange = (swapId: string, value: string) => {
    setHashInputs((prev) => ({ ...prev, [swapId]: value }));
  };

  const columns: GridColDef[] = BetSwapListColumns.map((col) => {
    if (col.field === 'hash') {
      return {
        ...col,
        renderCell: (params) => {
          const isEditable =
            (params.row.type === POINT_BET_SWAP_TYPE.POINT_TO_TOKEN ||
              params.row.type === POINT_BET_SWAP_TYPE.TOKEN_TO_POINT) &&
            params.row.status === POINT_BET_SWAP_STATUS.ING;

          if (isEditable) {
            return (
              <TextField
                size="small"
                fullWidth
                placeholder="Hash 입력"
                value={hashInputs[params.row.swapId] ?? params.row.hash ?? ''}
                onChange={(e) => handleHashChange(params.row.swapId, e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            );
          }
          return params.row.hash || '-';
        },
      };
    }
    return col;
  });

  const fetchSwapList = async () => {
    const re = await readSwapList();
    const data: AddIndexResReadAdminPointBetSwapDto[] = re.data.map(
      (item: ResReadAdminPointBetSwapDto, index: number) => ({
        ...item,
        id: item.swapId,
        index,
        tokenAmount: packBigNumbers(item.tokenAmount, 6),
        betAmount: packBigNumbers(item.betAmount),
        typeName: betSwapTypeFilter(item.type),
        statusName: betSwapStatusFilter(item.status),
      }),
    );
    setList(data);
  };

  useEffect(() => {
    fetchSwapList();
    return () => {};
  }, []);

  const navigate = useNavigate();

  const handleRowSelectionChange = (newSelection: GridSelectionModel) => {
    setSelectedRows(newSelection);
  };

  // 선택된 모든 행에 hash가 입력되었는지 확인
  const allHashesFilled = selectedRows.every((row) => {
    const swapId = row.toString();
    return hashInputs[swapId] && hashInputs[swapId].trim() !== '';
  });

  const handleBulkAction = async (action: 'approve' | 'reject') => {
    // 승인 시 hash 필수 체크
    if (action === 'approve' && !allHashesFilled) {
      toast.error('승인하려면 모든 항목에 Hash를 입력해주세요.');
      return;
    }

    console.log(`${action}할 항목들:`, selectedRows);
    setIsLoading(true);
    try {
      const re = await updateSwap(
        selectedRows.map((row) => {
          const swapId = row.toString();
          return {
            swap_id: swapId,
            status: action === 'approve' ? POINT_BET_SWAP_STATUS.COMPLETED : POINT_BET_SWAP_STATUS.REFUSED,
            ...(action === 'approve' ? { hash: hashInputs[swapId] } : {}),
          };
        }),
      );

      // 성공 시 리스트 다시 불러오기
      await fetchSwapList();
      toast.success('업데이트 성공');
      // 선택된 행들 및 hash 입력값 초기화
      setSelectedRows([]);
      setHashInputs({});
    } catch (error) {
      console.error('스왑 상태 업데이트 실패:', error);
      toast.error('업데이트 실패');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          overflow: 'auto',
          flex: 1,
          flexDirection: 'column',
          gap: { xs: '16px', sm: '0px' },
        }}
      >
        <Card>
          <Header title="스왑 리스트(전체)" />
          <Divider />
          <Box mb={2} />

          {selectedRows.length > 0 && (
            <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleBulkAction('approve')}
                disabled={!allHashesFilled}
              >
                선택 승인 ({selectedRows.length}개)
              </Button>
              <Button variant="contained" color="error" onClick={() => handleBulkAction('reject')}>
                선택 거절 ({selectedRows.length}개)
              </Button>
            </Box>
          )}

          <CardContent>
            {list && (
              <Box
                sx={{
                  width: '100%',
                  '& .MuiDataGrid-root': {
                    borderRadius: '10px',
                    overflow: 'hidden',
                  },
                  '& .MuiDataGrid-main': {
                    borderRadius: '10px',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    borderBottomLeftRadius: '10px',
                    borderBottomRightRadius: '10px',
                  },
                  '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                    paddingRight: '5px',
                  },
                }}
              >
                <DataGrid
                  sx={{
                    height: 'calc(100vh - 280px)',
                    border: 'none',
                    boxShadow: '0px 0px 4px 0px #00000026',
                    '& .MuiDataGrid-columnHeader': {
                      height: '46px !important',
                      background: theme.brand.primary,
                      color: '#ffffff',
                      typography: 'subtitle2',
                    },
                    '& .MuiDataGrid-columnHeader:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-columnHeader:selected': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-cell:focus-within': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-cell': {
                      outline: 'none',
                      typography: 'body2',
                      color: theme.greyscale.grey3,
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                  hideFooter
                  rows={list}
                  columns={columns}
                  getRowId={(row) => String(row.id)}
                  checkboxSelection
                  disableSelectionOnClick
                  isRowSelectable={(params) =>
                    (params.row.type === POINT_BET_SWAP_TYPE.POINT_TO_TOKEN ||
                      params.row.type === POINT_BET_SWAP_TYPE.TOKEN_TO_POINT) &&
                    params.row.status === POINT_BET_SWAP_STATUS.ING
                  }
                  onSelectionModelChange={handleRowSelectionChange}
                  selectionModel={selectedRows}
                  components={{
                    Toolbar: () => (
                      <div>
                        <GridToolbar />
                        <GridToolbarExport
                          csvOptions={{
                            fileName: 'swap_list',
                            utf8WithBom: true,
                          }}
                        />
                      </div>
                    ),
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
      <Loading isLoading={isLoading} />
    </>
  );
}
